import type { StyleSpecification } from 'maplibre-gl';

const C = {
  bg: '#314256',
  water: '#3367C2',
  green: '#2B4636',
  urban: '#37495E',
  bld: '#374A60',
  bldOut: '#2D4052',
  roadHi: '#6584A2',
  road4: '#536E8A',
  road5: '#47607A',
  road6: '#3C546A',
  road7: '#33485C',
  edge: '#23384C',
  edgeDark: '#1C2B3C',
  path: '#354C6A',
  border: '#3F5A72',
  rail: '#456482',
  textDark: '#EEF2F8',
  textMid: '#8AAAC8',
  textLight: '#587898',
  textDistr: '#6888A8',
  waterLabel: '#5878B0',
  halo: '#314256',
  haloWater: 'rgba(49,66,86,0.85)',
} as const;

export const taxiDarkNavyStyle: StyleSpecification = {
  version: 8,
  name: 'Taxi Dark Navy',
  sources: {
    openmaptiles: {
      type: 'vector',
      url: 'https://tiles.openfreemap.org/planet',
    },
  },
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: { 'background-color': C.bg },
    },
    {
      id: 'water-fill',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'water',
      paint: { 'fill-color': C.water, 'fill-antialias': true },
    },
    {
      id: 'waterway-line',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'waterway',
      paint: {
        'line-color': C.water,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 2.5],
        'line-opacity': 0.9,
      },
    },
    {
      id: 'landcover-green',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['in', 'class', 'grass', 'scrub', 'wood', 'forest', 'crop'],
      paint: { 'fill-color': C.green, 'fill-opacity': 0.9 },
    },
    {
      id: 'landcover-sand',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['in', 'class', 'sand', 'ice'],
      paint: { 'fill-color': '#283A4C' },
    },
    {
      id: 'landuse-park',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landuse',
      filter: [
        'in',
        'class',
        'park',
        'grass',
        'meadow',
        'garden',
        'pitch',
        'recreation_ground',
        'village_green',
        'playground',
      ],
      paint: { 'fill-color': C.green },
    },
    {
      id: 'landuse-cemetery',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landuse',
      filter: ['==', 'class', 'cemetery'],
      paint: { 'fill-color': '#22342E' },
    },
    {
      id: 'landuse-urban',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landuse',
      filter: [
        'in',
        'class',
        'residential',
        'commercial',
        'industrial',
        'retail',
        'school',
        'hospital',
        'university',
      ],
      paint: { 'fill-color': C.urban, 'fill-opacity': 0.6 },
    },
    {
      id: 'building-fill',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'building',
      minzoom: 14,
      paint: {
        'fill-color': C.bld,
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 14, 0, 15, 0.4, 15.5, 0.7, 16, 1],
      },
    },
    {
      id: 'building-outline',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'building',
      minzoom: 15,
      paint: {
        'line-color': C.bldOut,
        'line-width': 0.5,
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 15, 0, 16, 0.8],
      },
    },
    {
      id: 'boundary-country',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'boundary',
      filter: ['all', ['==', 'admin_level', 2], ['!=', 'maritime', 1]],
      paint: {
        'line-color': C.border,
        'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.4, 8, 0.8],
        'line-opacity': 0.7,
        'line-dasharray': [3, 2],
      },
    },
    {
      id: 'boundary-region',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'boundary',
      filter: ['all', ['==', 'admin_level', 4], ['!=', 'maritime', 1]],
      minzoom: 4,
      paint: {
        'line-color': C.border,
        'line-width': 0.4,
        'line-opacity': 0.5,
        'line-dasharray': [2, 3],
      },
    },
    {
      id: 'road-casing-motorway',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'motorway', 'trunk'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.edgeDark,
        'line-width': ['interpolate', ['linear'], ['zoom'], 7, 2, 12, 5, 16, 13, 18, 20],
        'line-gap-width': 0,
      },
    },
    {
      id: 'road-casing-primary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'primary', 'secondary'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.edge,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 12, 3.5, 16, 10, 18, 16],
      },
    },
    {
      id: 'road-casing-tertiary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['==', 'class', 'tertiary'],
      minzoom: 11,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.edge,
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 1, 14, 3, 16, 8],
      },
    },
    {
      id: 'road-casing-minor',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'minor', 'service', 'track'],
      minzoom: 13,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.edge,
        'line-width': ['interpolate', ['linear'], ['zoom'], 13, 1, 16, 5],
      },
    },
    {
      id: 'road-fill-motorway',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'motorway', 'trunk'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['interpolate', ['linear'], ['zoom'], 5, C.road7, 9, C.road6, 12, C.road5, 14, C.road4, 16, C.roadHi],
        'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1.5, 12, 4, 16, 11, 18, 18],
      },
    },
    {
      id: 'road-fill-primary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'primary', 'secondary'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['interpolate', ['linear'], ['zoom'], 8, C.road7, 12, C.road5, 15, C.roadHi],
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 12, 2.5, 16, 8, 18, 14],
      },
    },
    {
      id: 'road-fill-tertiary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['==', 'class', 'tertiary'],
      minzoom: 11,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.road4,
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.8, 14, 2.5, 16, 7],
      },
    },
    {
      id: 'road-fill-minor',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'minor', 'service'],
      minzoom: 13,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.roadHi,
        'line-width': ['interpolate', ['linear'], ['zoom'], 13, 0.6, 16, 4],
      },
    },
    {
      id: 'road-path',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'path', 'track', 'footway'],
      minzoom: 14,
      layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: {
        'line-color': C.path,
        'line-width': 1,
        'line-opacity': 0.7,
        'line-dasharray': [2, 2],
      },
    },
    {
      id: 'transit-rail',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'rail', 'subway', 'light_rail', 'tram', 'monorail', 'funicular'],
      minzoom: 11,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': C.rail,
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.8, 16, 2],
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 0.8],
      },
    },
    {
      id: 'transit-rail-dash',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'rail', 'subway'],
      minzoom: 13,
      paint: {
        'line-color': '#3A5070',
        'line-width': ['interpolate', ['linear'], ['zoom'], 13, 0.3, 16, 0.8],
        'line-dasharray': [3, 3],
        'line-opacity': 0.6,
      },
    },
    {
      id: 'aeroway',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'aeroway',
      minzoom: 10,
      paint: { 'fill-color': '#263650', 'fill-opacity': 0.7 },
    },
    {
      id: 'label-water',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'water_name',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Italic'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 12, 13],
        'text-max-width': 7,
        'symbol-placement': 'line',
        'text-transform': 'uppercase',
      },
      paint: {
        'text-color': C.waterLabel,
        'text-halo-color': C.haloWater,
        'text-halo-width': 1,
      },
    },
    {
      id: 'label-country',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'place',
      filter: ['==', 'class', 'country'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 3, 11, 8, 14],
        'text-max-width': 8,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.12,
      },
      paint: {
        'text-color': C.textLight,
        'text-halo-color': C.halo,
        'text-halo-width': 1.5,
      },
    },
    {
      id: 'label-city',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'place',
      filter: ['in', 'class', 'city', 'town'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 5, 11, 12, 15],
        'text-max-width': 10,
        'text-anchor': 'center',
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.08,
      },
      paint: {
        'text-color': C.textDark,
        'text-halo-color': C.halo,
        'text-halo-width': 1.5,
      },
    },
    {
      id: 'label-village',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'place',
      filter: ['in', 'class', 'village', 'suburb', 'neighbourhood', 'hamlet'],
      minzoom: 11,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'text-max-width': 8,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.05,
      },
      paint: {
        'text-color': C.textDistr,
        'text-halo-color': C.halo,
        'text-halo-width': 1,
      },
    },
    {
      id: 'label-road',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'transportation_name',
      minzoom: 12,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'symbol-placement': 'line',
        'text-max-angle': 30,
        'text-padding': 2,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.04,
      },
      paint: {
        'text-color': C.textMid,
        'text-halo-color': 'rgba(58,78,98,0.9)',
        'text-halo-width': 1.5,
      },
    },
    {
      id: 'label-poi',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'poi',
      minzoom: 24,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'text-max-width': 8,
        'text-anchor': 'top',
        'text-offset': [0, 0.4],
        'text-transform': 'uppercase',
      },
      paint: {
        'text-color': C.textDark,
        'text-halo-color': 'rgba(58,78,98,0.8)',
        'text-halo-width': 1,
      },
    },
  ],
};





const C2 = {
  bg: '#F9F7F2',
  water: '#DCE9F5',
  green: '#E6F2E3',
  urban: '#F2F0E8',
  bld: '#F3F0E7',
  bldOut: '#E5E1D4',
  roadHi: '#FFFFFF',
  road4: '#F7F5EB',
  road5: '#F2F0E4',
  road6: '#EDEBDF',
  road7: '#E6E3D3',
  edge: '#D1CBAB',
  edgeDark: '#C0BAAA',
  path: '#C3BC95',
  border: '#B3B1A4',
  rail: '#C8C5D8',
  textDark: '#5C5849',
  textMid: '#75705E',
  textLight: '#9E9983',
  textDistr: '#A6A0A0',
  waterLabel: '#91A1B3',
  halo: '#F9F7F2',
  haloWater: 'rgba(249,247,242,0.85)',
} as const;

export const taxiWarmNeutralStyle: StyleSpecification = {
  version: 8,
  name: 'Taxi Warm Neutral',
  sources: {
    openmaptiles: {
      type: 'vector',
      url: 'https://tiles.openfreemap.org/planet',
    },
  },
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: { 'background-color': C.bg },
    },
    {
      id: 'water-fill',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'water',
      paint: { 'fill-color': C.water, 'fill-antialias': true },
    },
    {
      id: 'waterway-line',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'waterway',
      paint: {
        'line-color': C.water,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 2.5],
        'line-opacity': 0.9,
      },
    },
    {
      id: 'landcover-green',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['in', 'class', 'grass', 'scrub', 'wood', 'forest', 'crop'],
      paint: { 'fill-color': C.green, 'fill-opacity': 0.9 },
    },
    {
      id: 'landcover-sand',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['in', 'class', 'sand', 'ice'],
      paint: { 'fill-color': '#F0EDDF' },
    },
    {
      id: 'landuse-park',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landuse',
      filter: [
        'in',
        'class',
        'park',
        'grass',
        'meadow',
        'garden',
        'pitch',
        'recreation_ground',
        'village_green',
        'playground',
      ],
      paint: { 'fill-color': C.green },
    },
    {
      id: 'landuse-cemetery',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landuse',
      filter: ['==', 'class', 'cemetery'],
      paint: { 'fill-color': '#E2EDD8' },
    },
    {
      id: 'landuse-urban',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landuse',
      filter: [
        'in',
        'class',
        'residential',
        'commercial',
        'industrial',
        'retail',
        'school',
        'hospital',
        'university',
      ],
      paint: { 'fill-color': C.urban, 'fill-opacity': 0.6 },
    },
    {
      id: 'building-fill',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'building',
      minzoom: 14,
      paint: {
        'fill-color': C.bld,
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 14, 0, 15, 0.4, 15.5, 0.7, 16, 1],
      },
    },
    {
      id: 'building-outline',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'building',
      minzoom: 15,
      paint: {
        'line-color': C.bldOut,
        'line-width': 0.5,
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 15, 0, 16, 0.8],
      },
    },
    {
      id: 'boundary-country',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'boundary',
      filter: ['all', ['==', 'admin_level', 2], ['!=', 'maritime', 1]],
      paint: {
        'line-color': C.border,
        'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.4, 8, 0.8],
        'line-opacity': 0.7,
        'line-dasharray': [3, 2],
      },
    },
    {
      id: 'boundary-region',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'boundary',
      filter: ['all', ['==', 'admin_level', 4], ['!=', 'maritime', 1]],
      minzoom: 4,
      paint: {
        'line-color': C.border,
        'line-width': 0.4,
        'line-opacity': 0.5,
        'line-dasharray': [2, 3],
      },
    },
    {
      id: 'road-casing-motorway',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'motorway', 'trunk'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.edgeDark,
        'line-width': ['interpolate', ['linear'], ['zoom'], 7, 2, 12, 5, 16, 13, 18, 20],
        'line-gap-width': 0,
      },
    },
    {
      id: 'road-casing-primary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'primary', 'secondary'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.edge,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 12, 3.5, 16, 10, 18, 16],
      },
    },
    {
      id: 'road-casing-tertiary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['==', 'class', 'tertiary'],
      minzoom: 11,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.edge,
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 1, 14, 3, 16, 8],
      },
    },
    {
      id: 'road-casing-minor',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'minor', 'service', 'track'],
      minzoom: 13,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.edge,
        'line-width': ['interpolate', ['linear'], ['zoom'], 13, 1, 16, 5],
      },
    },
    {
      id: 'road-fill-motorway',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'motorway', 'trunk'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['interpolate', ['linear'], ['zoom'], 5, C.road7, 9, C.road6, 12, C.road5, 14, C.road4, 16, C.roadHi],
        'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1.5, 12, 4, 16, 11, 18, 18],
      },
    },
    {
      id: 'road-fill-primary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'primary', 'secondary'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['interpolate', ['linear'], ['zoom'], 8, C.road7, 12, C.road5, 15, C.roadHi],
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 12, 2.5, 16, 8, 18, 14],
      },
    },
    {
      id: 'road-fill-tertiary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['==', 'class', 'tertiary'],
      minzoom: 11,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.road4,
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.8, 14, 2.5, 16, 7],
      },
    },
    {
      id: 'road-fill-minor',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'minor', 'service'],
      minzoom: 13,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': C.roadHi,
        'line-width': ['interpolate', ['linear'], ['zoom'], 13, 0.6, 16, 4],
      },
    },
    {
      id: 'road-path',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'path', 'track', 'footway'],
      minzoom: 14,
      layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: {
        'line-color': C.path,
        'line-width': 1,
        'line-opacity': 0.7,
        'line-dasharray': [2, 2],
      },
    },
    {
      id: 'transit-rail',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'rail', 'subway', 'light_rail', 'tram', 'monorail', 'funicular'],
      minzoom: 11,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': C.rail,
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.8, 16, 2],
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 0.8],
      },
    },
    {
      id: 'transit-rail-dash',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'rail', 'subway'],
      minzoom: 13,
      paint: {
        'line-color': '#FFFFFF',
        'line-width': ['interpolate', ['linear'], ['zoom'], 13, 0.3, 16, 0.8],
        'line-dasharray': [3, 3],
        'line-opacity': 0.6,
      },
    },
    {
      id: 'aeroway',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'aeroway',
      minzoom: 10,
      paint: { 'fill-color': '#EBE8D8', 'fill-opacity': 0.7 },
    },
    {
      id: 'label-water',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'water_name',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Italic'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 12, 13],
        'text-max-width': 7,
        'symbol-placement': 'line',
      },
      paint: {
        'text-color': C.waterLabel,
        'text-halo-color': C.haloWater,
        'text-halo-width': 1,
      },
    },
    {
      id: 'label-country',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'place',
      filter: ['==', 'class', 'country'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 3, 11, 8, 14],
        'text-max-width': 8,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.12,
      },
      paint: {
        'text-color': C.textLight,
        'text-halo-color': C.halo,
        'text-halo-width': 1.5,
      },
    },
    {
      id: 'label-city',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'place',
      filter: ['in', 'class', 'city', 'town'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 5, 11, 12, 15],
        'text-max-width': 10,
        'text-anchor': 'center',
      },
      paint: {
        'text-color': C.textDark,
        'text-halo-color': C.halo,
        'text-halo-width': 1.5,
      },
    },
    {
      id: 'label-village',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'place',
      filter: ['in', 'class', 'village', 'suburb', 'neighbourhood', 'hamlet'],
      minzoom: 11,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'text-max-width': 8,
      },
      paint: {
        'text-color': C.textDistr,
        'text-halo-color': C.halo,
        'text-halo-width': 1,
      },
    },
    {
      id: 'label-road',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'transportation_name',
      minzoom: 12,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'symbol-placement': 'line',
        'text-max-angle': 30,
        'text-padding': 2,
      },
      paint: {
        'text-color': C.textMid,
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.5,
      },
    },
    {
      id: 'label-poi',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'poi',
      minzoom: 24,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'text-max-width': 8,
        'text-anchor': 'top',
        'text-offset': [0, 0.4],
      },
      paint: {
        'text-color': C.textDark,
        'text-halo-color': 'rgba(255,255,255,0.8)',
        'text-halo-width': 1,
      },
    },
  ],
};
